import * as React from 'react';
import errorResponses from '../../errorResponses';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { IDocument } from '../../types/my-types';
import { saveAs } from 'file-saver';
import { useQueryClient } from '@tanstack/react-query';
import Tooltip from '@mui/material/Tooltip';
import { Button, useTheme } from '@mui/material';
import FileDownload from '@mui/icons-material/FileDownload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import IconButtonWhiteBg from './IconButtonWhiteBg';
import DownloadIcon from '@mui/icons-material/Download';

interface Props {
  documentId: string;
  documentContentType: string;
  documentLabel: string;
  documentFileExtension?: string;
  iconType: 'cloud' | 'file' | 'button' | 'iconButtonWhiteBg';
}

function DownloadDocument({ documentId, documentContentType, documentLabel, documentFileExtension, iconType }: Props) {
  const { setToast } = React.useContext(AppContext);
  const client = useQueryClient();
  const glynt = useGlynt();
  const theme = useTheme();

  const download = () => {
    setToast({
      open: true,
      message: 'Downloading document',
      variant: 'success',
    });
    client.fetchQuery({
        queryKey: ['docFile'],
        queryFn: async () => (await glynt).document.getFile(documentId),
      })
      .then((data) => {
        const fileURL = data.file_temp_url;

        fetch(fileURL, {
          method: 'get',
          headers: new Headers({
            accept: documentContentType,
          }),
        }).then((data) => {
          if (data.status === 200) {
            data.blob().then((text) => {
              // Use file_extension if provided, otherwise fall back to deriving from content_type
              const extension = documentFileExtension || `.${documentContentType.split('/')[1]}`;
              saveAs(text, `${documentLabel}${extension}`);
            });
          } else {
            setToast({
              open: true,
              message: errorResponses.failedToDownloadDocument,
              variant: 'error',
            });
          }
        });
      })
      .catch(() => {
        setToast({
          open: true,
          message: errorResponses.failedToDownloadDocument,
          variant: 'error',
        });
      });
  };

  const tooltipTitle = `Download Document`;
  return (
    <>
      {iconType === 'cloud' ? (
        <Tooltip placement="top" title={tooltipTitle} disableFocusListener disableTouchListener>
          <CloudDownloadIcon
            data-cy={`btn-manage-download-${documentLabel}`}
            onClick={() => download()}
            style={{ color: theme.palette.custom.lightGreen, cursor: 'pointer' }}
          />
        </Tooltip>
      ) : iconType === 'file' ? (
        <Tooltip placement="top" title={tooltipTitle} disableFocusListener disableTouchListener>
          <FileDownload
            data-cy={`btn-manage-download-${documentLabel}`}
            onClick={() => download()}
            style={{ color: theme.palette.custom.lightGreen, cursor: 'pointer' }}
          />
        </Tooltip>
      ) : iconType === 'iconButtonWhiteBg' ? (
        <IconButtonWhiteBg
          disabled={false}
          onClick={() => download()}
          id={`btn-manage-download-${documentLabel}`}
          cypressId={`btn-manage-download-${documentLabel}`}
          tooltip="Download"
        >
          <DownloadIcon style={{ fontSize: '12px', color: 'black' }} />
        </IconButtonWhiteBg>
      ) : (
        <Tooltip placement="top" title={tooltipTitle} disableFocusListener disableTouchListener>
          <Button data-cy={`btn-manage-download-${documentLabel}`} onClick={() => download()}>
            Download
          </Button>
        </Tooltip>
      )}
    </>
  );
}

export default DownloadDocument;
